import _ from 'lodash';
import { getAllCameras, getCamerasStatus, getConfigForCameras, getAnalyticsRulesForCameras, RESOLUTION_TO_NVRMODE_MAP } from '../api/cameras';
import { getAllViewerSettingsForCameras, getUserSchedulesForCameras } from '../api/user';
import { getUserDevices } from '../api/support';
import { getLineCrossings } from '../api/peopleCounting';
import { supportedAddons } from './plan';
import { SETTINGS_SECTIONS } from '../pages/camera-settings/utils/settings';
import { getCameraStatus } from './status';
import { sessionId } from '../components/app/App';
import { getCamerasAnalyticsAreas } from '../api/vehicles';
import { msToDays } from './datetime';

export const getThumbURL = (logger, uidd, lastThumbTime, token) => {
    return `https://${logger}/getthumb/${uidd}/${lastThumbTime}/${token}`;
}

export const getLiveThumbURL = (logger, uidd, lastThumbTime, token) => {
    return `https://${logger}/livethumb/${uidd}/${lastThumbTime}/${token}?sessionId=${sessionId}`;
}


export const compatibleCameras = ['Hikvision', 'Dahua', 'Lorex', 'LTS', 'Uniview', 'Vivotek', 'Axis', 'Hanwha/Wisenet'];

/**
 * Returns UI state based on camera status, whether camera is currently live streaming, whether we want
 * camera to live stream, and whether live stream is buffering.
 * 
 * Possible UI states:
 *     * Live - camera live stream is playing
 *            - cameraStatus is 'online', showLiveStream is true, liveStreaming is true and buffering is false
 *     * Loading - waiting for live stream to play
 *               - cameraStatus is 'online', showLiveStream is true, liveStreaming is false or buffering is true
 *     * Online - camera is online and we don't want it to live stream
 *              - cameraStatus is 'online', showLiveStream is false
 *     * Offline - camera is offline
 *               - cameraStatus is 'offline'
 *     * Standby - camera is on standby
 *               - cameraStatus is 'standby'
 *     * No video - camera sending no video
 *                - cameraStatus is 'novideo'
 * 
 * This function is used by Multicam, Singlecam and related components.
 * 
 * @param {('online', 'offline', 'standby', 'novideo')} cameraStatus - camera status
 * @param {boolean} liveStreaming - whether camera is live streaming
 * @param {boolean} showLiveStream - whether we want to show camera live stream
 * @param {boolean} liveStreamLoading - whether ReactPlayer is still starting up (i.e. onBufferEnd has not been called once yet)
 * @param {boolean} liveStreamBuffering - whether ReactPlayer is buffering (does not include initial buffering when loading up)
 * @param {boolean} videoUnplayable - whether we are unable to play to video (e.g. because browser doesn't support codec)
 * @returns {('live', 'loading', 'buffering', 'online', 'offline', 'standby', 'novideo', 'videoUnplayable')} - UI state
 */
export const getCameraUIState = (cameraStatus, liveStreaming, showLiveStream, liveStreamLoading, liveStreamBuffering, videoUnplayable, camId, cloudAdapterId) => {

    if (cameraStatus === 'online' && showLiveStream) {
        if (videoUnplayable) {
            return 'videoUnplayable';
        }
        if (!liveStreaming || liveStreamLoading) {
            return 'loading';
        }
        if (liveStreamBuffering) {
            return 'buffering';
        }
        return 'live';
    }

    return getCameraStatus({ status: cameraStatus, cloudRecordingEnabled: true, camId, cloudAdapterId });
};


// Returns all cameras that user has access to (including non-admin), along with their status info
export const getAllCamerasWithStatus = async (uidd) => {
    const cameras = await getAllCameras({ uidd });
    const statuses = await getCamerasStatus(Object.values(cameras));

    return _.mapValues(cameras, (camera, uidd) => ({
        ...camera,
        ...statuses[uidd]
    }));
};

const mergeArrayToMap = (arr) => {
    if (!arr?.length) return {};
    return Object.assign({}, ...arr);
};

export const getCameraConfigs = async (sections=['main'], permissionRestriction = ['a', 'r'], filterCameras, uid, uidds, isManager = false, isDemo = false, region, cb=()=>{}) => {
    let cameras = {};
    let cameraKeys = uidds;

    if (sections.includes('main')) {
        cameras = await getMainCameraData(uid, uidds, permissionRestriction, isManager, isDemo);
        if (!cameraKeys) {
            cameraKeys = Object.keys(cameras);
        }
    }

    const configs = await getCamerasConfigsForSections(sections, uid, cameraKeys, isManager);

    const camerasWithAllSettings = cameraKeys.reduce((acc, uidd) => {
        acc[uidd] = {
            ...(cameras[uidd] || {}),
            ...(configs[uidd] || {})
        };
        return acc;
    }, {});

    cb();

    return filterCameras ? filterCameras(camerasWithAllSettings) : camerasWithAllSettings;
};

const getMainCameraData = async (uid, uidds, permissionRestriction, isManager, isDemo) => {
    let allCameras;
    
    if (isManager) {
        const allUserDevices = await getUserDevices(uid);
        
        allCameras = allUserDevices.cameras.reduce((acc, device) => {
            const camId = parseInt(device.id);
            const uidd = `${uid}.${camId}`;
            
            acc[uidd] = {
                ...device,
                cloudAdapterId: device.adaptr_cloud_id,
                uidd: uidd,
                name: device.phonename,
                camId: device.phoneid,
                access: 'a',
                recordingResolution: _.invert(RESOLUTION_TO_NVRMODE_MAP)[device.nvr_mode],
                romEnabled: device.recording_mode === "rom",
                cloudRecordingEnabled: device.cloud_recording_enabled === 1
            };
            
            return acc;
        }, {});
    } else {
        allCameras = await getAllCamerasWithStatus(uidds);
    }

    if (isManager || isDemo) return allCameras;
    
    return _.pickBy(allCameras, ({ access }) => permissionRestriction.includes(access));
};

export const getCamerasConfigsForSections = async (sections, uid, uidds, isManager = false) => {
    if (!uidds?.length) return {};

    const includesMain = sections.includes("main");
    const includesAlerts = sections.includes(SETTINGS_SECTIONS.ALERTS);
    const includesAnalytics = sections.includes(SETTINGS_SECTIONS.ANALYTICS);

    const promises = [];
    const promiseKeys = [];

    if (includesMain) {
        promises.push(getConfigForCameras(uidds, uid, isManager));
        promiseKeys.push('camerasSettings');
        promises.push(getUserSchedulesForCameras(uidds, uid, isManager));
        promiseKeys.push('schedules');
    }

    if (includesAlerts) {
        promises.push(getAllViewerSettingsForCameras(uidds, uid, isManager));
        promiseKeys.push('viewerSettings');
    }

    if (includesAnalytics) {
        promises.push(getAnalyticsRulesForCameras(uidds, isManager));
        promiseKeys.push('alertRules');
        promises.push(getCamerasAnalyticsAreas(uidds, isManager));
        promiseKeys.push('detectionZones');

        if (!isManager) {
            promises.push(getLineCrossings(uidds));
            promiseKeys.push('lineCrossings');
        }
    }
    
    const results = await Promise.all(promises);
    
    const resultsMap = {};
    promiseKeys.forEach((key, index) => {
        resultsMap[key] = results[index];
    });
    
    const settingsObj = includesMain ? mergeArrayToMap(resultsMap.camerasSettings) : {};
    const schedulesObj = includesMain ? mergeArrayToMap(resultsMap.schedules) : {};
    const viewerSettingsObj = includesAlerts ? mergeArrayToMap(resultsMap.viewerSettings) : {};
    const alertsObj = includesAnalytics ? mergeArrayToMap(resultsMap.alertRules) : {};
    const detectionZonesObj = includesAnalytics ? mergeArrayToMap(resultsMap.detectionZones) : {};
    
    const lineCrossingsObj = {};
    if (includesAnalytics && !isManager && resultsMap.lineCrossings) {
        for (const line of resultsMap.lineCrossings) {
            lineCrossingsObj[line.uidd] = lineCrossingsObj[line.uidd] || [];
            lineCrossingsObj[line.uidd].push(line);
        }
    }
    
    const allSettings = {};
    
    for (const uidd of uidds) {
        const setting = {
            ...settingsObj[uidd]
        };
        
        if (includesAlerts) {
            Object.assign(setting, viewerSettingsObj[uidd]);
        }
        
        if (includesMain) {
            setting.schedules = schedulesObj[uidd];
        }
        
        if (includesAlerts) {
            setting.alertRules = alertsObj[uidd];
        }
        
        if (includesAnalytics) {
            const zones = detectionZonesObj[uidd] || [];
            const lines = lineCrossingsObj[uidd] || [];
            
            setting.areas = zones.map(({ coordinates, ...polygon }) => ({
                ...polygon,
                coordinates: coordinates.map(([x, y]) => [x * 0.01, y * 0.01]),
                scheme: ANALYTICS_TYPES.LPR // HARDCODED SCHEME FOR LPR, there is no other scheme using areas for now..
            }));
            setting.lines = lines;
           lines.forEach(line => {
                const key = `${line.scheme}lineCount`;
                setting[key] = (setting[key] || 0) + 1;
            });
            setting.lineCount = lines.length;
            setting.polygonCount = zones.length;
        }
        
        allSettings[uidd] = setting;
    }
    
    return allSettings;
};

export const getAnalyticsQuotaFromFormValues = (formValues) => {
    const analyticsQuota = {};
    for(let addon of supportedAddons) {
        if (formValues[addon] > 0) {
            analyticsQuota[addon] = formValues[addon];
        }
    }
    return _.isEmpty(analyticsQuota) ? undefined : analyticsQuota;
};


export const getAllCamerasWithCloudStorageDuration = async (organisations, accessToFilterOut = ['l']) => {
    const storageDurations = organisations.reduce((acc, { id, cloudStorageDuration }) => ({ ...acc, [id]: msToDays(cloudStorageDuration) }), {});

    const cameras = await getAllCameras();

    return _.chain(cameras).pickBy(({ access }) => !accessToFilterOut.includes(access)).mapValues(camera => ({ ...camera, cloudStorageDuration: storageDurations[camera.owner] })).value();
};

// addon "smart-motion" for analytics scheme "smart"
// addon "people" for analytics scheme "people-lc"
export const supportedAnalyticsSchemes = ['videoloft', 'people-lc', 'lpr', 'ppe', 'videoloftexp', 'smart', 'orders'];

export const ANALYTICS_TYPES = {
    PEOPLE: 'peopleCounting',
    LPR: 'lpr',
    PPE: 'ppe',
    VIDEOLOFT: 'videoloft',
    OBJECTS: 'videoloftexp',
    SMART: 'smart',
    ORDERS: 'orders'
};

export const analyticsSchemes = {
    peopleCounting: 'people-lc',
    lpr: 'lpr',
    ppe: 'ppe',
    videoloft: 'videoloft',
    'smart-motion': 'smart',
    'orders': 'orders'
};

